import { api, wire, LightningElement } from "lwc";
import { Config, Image } from "./types";
import {
  ResizeObserverAdapter,
  ResizeObserverEntry,
} from "common/resizeObserverAdapter";
import { attrToBool } from "common/helpers";

type Size = "small" | "medium" | "large";

const widthBreakpoints: Record<Size, number> = {
  small: 400,
  medium: 800,
  large: Infinity,
};

function isDisplayableImage(image: Image | undefined) {
  return !!(image && image.src);
}

export default class extends LightningElement {
  @api config: Config = {};
  @api dismissible: boolean | string | null = null;

  size: Size = "large";

  @wire(ResizeObserverAdapter, {
    template: "$template",
  })
  private onResize(entry: ResizeObserverEntry) {
    const width = entry.contentRect.width;
    if (width < widthBreakpoints.small) {
      this.size = "small";
    } else if (width < widthBreakpoints.medium) {
      this.size = "medium";
    } else {
      this.size = "large";
    }
  }

  private get isDismissible() {
    return attrToBool(this.dismissible);
  }

  private get className() {
    return `promo size--${this.size}`;
  }

  private get styles() {
    return {
      container: this.getContainerStyles(),
      content: this.getContentStyles(),
    };
  }

  private getContainerStyles() {
    let style = "";
    const background = this.config.background;

    style += background?.color ? `background-color: ${background.color};` : "";
    style += background?.img ? `background-image: url(${background.img});` : "";
    style += background?.position
      ? `background-position: ${background.position};`
      : "";
    style += background?.repeat
      ? `background-repeat: ${background.repeat};`
      : "";
    style += background?.size ? `background-size: ${background.size};` : "";
    style += background?.padding ? `padding: ${background.padding};` : "";

    return style;
  }

  private get hasMainImage() {
    return isDisplayableImage(this.config.img);
  }

  private get content() {
    return this.config.content || {};
  }

  private getContentStyles() {
    const color = this.content.color;
    return color ? `color: ${color}` : "";
  }

  private get hasContentImage() {
    return isDisplayableImage(this.content.img);
  }

  private get cta() {
    return this.config.cta;
  }

  private get showCtaLink() {
    return !!this.cta?.link?.href;
  }

  private get showCtaButton() {
    return !this.showCtaLink && this.cta?.label;
  }

  private get ctaVariant() {
    if (this.cta?.variant) {
      return this.cta.variant;
    }

    return "brand";
  }

  private get close() {
    return this.config.close;
  }

  handleCtaClick() {
    this.dispatchEvent(new CustomEvent("promo_cta_clicked"));
  }

  handleRequestClose() {
    this.dispatchEvent(new CustomEvent("requestclose"));
  }
}
